/**
 * f13.1: request a cycle CSV export, poll it, download it.
 *
 * A TanStack mutation queues the job; a status query polls every 2 s and stops
 * by itself on a terminal state; on SUCCEEDED the file is fetched as a blob
 * through the typed client (bearer token attached there, never here) and
 * handed to the browser. Scope is the server's -- nothing here filters rows.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiError, api, type CycleExportStatus } from '@/api/client';
import { JOB_POLL_INTERVAL_MS, isJobTerminal } from '@/api/queries';
import { queryKeys } from '@/api/queryKeys';

export type ExportOutcome =
  | { tone: 'success'; message: string }
  | { tone: 'error'; message: string };

const RESULTS_NOT_CURRENT = 'RESULTS_NOT_CURRENT';
const RECALCULATING = 'Recalculation in progress — try again in a few seconds.';

/** Hand a blob to the browser as a named download. */
export function saveBlob(blob: Blob, fileName: string): void {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

export function failureMessage(status: CycleExportStatus): string {
  if (status.error_code === RESULTS_NOT_CURRENT) return RECALCULATING;
  return status.error_message ?? 'The export failed.';
}

export function successMessage(rowCount: number | null): string {
  const count = rowCount ?? 0;
  return `Exported ${count} engagement${count === 1 ? '' : 's'}`;
}

export function useCycleExport(cycleId: string) {
  const [exportId, setExportId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ExportOutcome | null>(null);
  const handled = useRef<string | null>(null);

  const request = useMutation({
    mutationFn: () => api.requestCycleExport(cycleId),
    onMutate: () => {
      setOutcome(null);
      handled.current = null;
    },
    onSuccess: (accepted) => setExportId(accepted.export_id),
    onError: (error) =>
      setOutcome({
        tone: 'error',
        message: error instanceof ApiError ? error.message : 'The export could not be requested.',
      }),
  });

  const status = useQuery({
    queryKey: queryKeys.cycles.export(cycleId, exportId ?? ''),
    queryFn: () => api.getCycleExport(cycleId, exportId as string),
    enabled: Boolean(exportId),
    refetchInterval: (query) =>
      isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS,
    staleTime: 0,
    retry: 2,
  });

  const current = status.data;
  useEffect(() => {
    if (!current || !exportId || handled.current === exportId || !isJobTerminal(current.state)) return;
    handled.current = exportId;
    if (current.state !== 'SUCCEEDED') {
      setOutcome({ tone: 'error', message: failureMessage(current) });
      setExportId(null);
      return;
    }
    api
      .downloadCycleExport(cycleId, exportId)
      .then((blob) => {
        saveBlob(blob, current.file_name);
        setOutcome({ tone: 'success', message: successMessage(current.row_count) });
      })
      .catch((error: unknown) =>
        setOutcome({
          tone: 'error',
          message: error instanceof ApiError ? error.message : 'The file could not be downloaded.',
        }),
      )
      .finally(() => setExportId(null));
  }, [current, cycleId, exportId]);

  const preparing = request.isPending || exportId !== null;
  return {
    start: () => request.mutate(),
    preparing,
    progress: current && !isJobTerminal(current.state) ? current.progress_percent : 0,
    outcome,
    dismiss: () => setOutcome(null),
  };
}
